import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { OperatorTypes } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import PaginatedListNavigation
  from '../../../paginatedlist/PaginatedListNavigation/PaginatedListNavigation';
import { mapApiChannelToTableRow } from '../../../paginatedchannellist/channelTableViewModel';
import { parseChannelKey } from '../../../../utility/channelUtils';
import RaaAuthenticatedRemoteService from '../../../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../../../envConfig';
import { selectSessionInformation } from '../../../../redux/slices/sessionSlice';
import {
  selectRoomAlertDeviceList,
  selectSensorMap,
} from '../../../../redux/slices/deviceList/deviceListSlice';
import useFilters from '../../../hooks/useFilters/useFilters';
import SearchType from '../../../../utility/types/SearchType';
import facetType from '../../../../utility/types/facetType';
import ChannelTypeFacet from '../../../../classes/facets/ChannelTypeFacet/ChannelTypeFacet';
import DeviceSelector from '../../../xlalerts/alertforms/SelectSensorsSection/DeviceSelector';
import ChannelTypeSelector from '../../../xlalerts/alertforms/SelectSensorsSection/ChannelTypeSelector';
import ChannelPickerTable from './ChannelPickerTable';

const DEFAULT_COLUMN_VISIBILITY = {
  DeviceName: true,
  SensorName: true,
  ComponentName: true,
  SensorType: true,
  SensorChannel: true,
  Port: true,
  LastReading: true,
  LastReceived: true,
  DeviceType: false,
  MACAddress: false,
  SerialNumber: false,
  Alerts: false, // no alert integration in the widget picker
};

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_CHANNELS_SEARCH_URL = `${envConfig.apiUrl}/api/v1/device-data/channels/fetch-paginated-and-filtered`;

const ChannelSelectorModal = ({
  show,
  onHide,
  selectedChannelId,
  onSelectChannel,
  title,
  hideChannelTypeSelector,
  endpointUrl,
}) => {
  const deviceList = useSelector(selectRoomAlertDeviceList);
  const sensorMap = useSelector(selectSensorMap);
  const sessionInformation = useSelector(selectSessionInformation);

  const channelTypeFacet = useMemo(() => new ChannelTypeFacet(), []);
  const channelTypeSelectOptions = channelTypeFacet.getValueSelectOptions();

  const [rawChannels, setRawChannels] = useState([]);
  const [pageNo, setPageNo] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState(null);
  const [sortType, setSortType] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState(DEFAULT_COLUMN_VISIBILITY);

  const {
    filters,
    setFilters,
    updateFilters,
    getFilterByFacetTypeId,
  } = useFilters({
    initialFilters: [],
    searchType: SearchType.CHANNEL,
  });

  const selectedDeviceFilters = getFilterByFacetTypeId(facetType.DEVICE_ID);
  const selectedDevices = deviceList
    .filter((d) => selectedDeviceFilters.some((f) => f.value1 === d.id))
    .map((d) => ({ label: d.displayName, value: d.id }));

  const selectedChannelTypeFilters = getFilterByFacetTypeId(facetType.CHANNEL_TYPE);
  const selectedChannelOptions = channelTypeSelectOptions
    .filter((opt) => opt.value.some(
      (val) => selectedChannelTypeFilters.some((f) => f.value1 === val),
    ));

  const handleDeviceChange = (devices) => {
    // eslint-disable-next-line no-nested-ternary
    const normalized = Array.isArray(devices) ? devices : (devices ? [devices] : []);
    const deviceUuids = normalized.map((opt) => opt.value);
    const newFilters = deviceUuids.map((uuid) => ({
      id: SearchType.CHANNEL,
      facetId: facetType.DEVICE_ID,
      operatorId: OperatorTypes.EQUAL_TO_VALUE,
      value1: uuid,
      value2: '',
    }));
    updateFilters(newFilters, facetType.DEVICE_ID);
  };

  const handleChannelTypeChange = (options) => {
    // eslint-disable-next-line no-nested-ternary
    const normalized = Array.isArray(options) ? options : (options ? [options] : []);
    const flattenedValues = normalized.flatMap((opt) => opt.value || []);
    const newFilters = flattenedValues.map((val) => ({
      id: SearchType.CHANNEL,
      facetId: facetType.CHANNEL_TYPE,
      operatorId: OperatorTypes.EQUAL_TO_VALUE,
      value1: val,
      value2: '',
    }));
    updateFilters(newFilters, facetType.CHANNEL_TYPE);
  };

  const handleClearFacets = () => {
    setFilters([]);
  };

  useEffect(() => {
    if (!show) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const payload = {
      data: {
        id: null,
        name: null,
        filters: filters || [],
        sort,
        sortType,
        pageNo,
        pageSize: rowsPerPage,
      },
    };

    const url = endpointUrl || DEFAULT_CHANNELS_SEARCH_URL;

    RaaAuthenticatedRemoteService.globalInstance()
      .post(url, payload)
      .then((response) => {
        if (cancelled) return;
        const results = (response && response.searchResult) || [];
        const pageInfo = (response && response.pageInfo) || {};
        setRawChannels(results);
        setTotalRecords(pageInfo.totalRecords || 0);
        setHasNextPage(!!pageInfo.hasNext);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        setRawChannels([]);
        setTotalRecords(0);
        setHasNextPage(false);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [show, filters, pageNo, rowsPerPage, sort, sortType, endpointUrl]);

  useEffect(() => {
    setPageNo(0);
  }, [filters, sort, sortType, rowsPerPage]);

  useEffect(() => {
    if (show) {
      setFilters([]);
      setPageNo(0);
      setSort(null);
      setSortType(null);
    }
  }, [show]);

  const getDevice = (mac) => deviceList.find((d) => d.uuid === mac);
  const getTargetSensor = (channelKey) => {
    const {
      deviceMacAddress, sensorMacAddress, channelGroupType, channelGroupIndex,
    } = parseChannelKey(channelKey);
    const channelGroupList = sensorMap[deviceMacAddress] || [];
    const identifier = `${sensorMacAddress}_${channelGroupType}_${channelGroupIndex}`;
    return channelGroupList.find(
      (s) => `${s.channelGroupMacAddress}_${s.channelGroupTypeId}_${s.channelGroupIndex}` === identifier,
    );
  };

  const displayOptions = {
    scale: sessionInformation.temperatureScale,
    timeZone: sessionInformation.timeZone,
  };

  const formattedChannelList = useMemo(
    () => rawChannels.map(
      (c) => mapApiChannelToTableRow(c, displayOptions, getDevice, getTargetSensor),
    ),
    [rawChannels, displayOptions.scale, displayOptions.timeZone],
  );

  const handleAddChannel = (row) => {
    const raw = rawChannels.find((c) => c.channelKey === row.channelKey);
    if (!raw) return;
    onSelectChannel({ raw, row });
  };

  const handleSortChange = (nextSort, nextSortType) => {
    setSort(nextSort);
    setSortType(nextSortType);
  };

  const handleToggleColumn = (name, visible) => {
    setColumnVisibility((prev) => ({ ...prev, [name]: visible }));
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ overflowX: 'auto' }}>
        <div className="row align-items-center">
          <div className={hideChannelTypeSelector ? 'col-10' : 'col-5'}>
            <DeviceSelector
              onUpdateSelectedDevices={handleDeviceChange}
              selectedDevices={selectedDevices}
              deviceList={deviceList}
            />
          </div>
          {!hideChannelTypeSelector && (
            <div className="col-5">
              <ChannelTypeSelector
                onUpdateSelectedChannelType={handleChannelTypeChange}
                channelTypeList={channelTypeSelectOptions}
                selectedChannelOptions={selectedChannelOptions}
              />
            </div>
          )}
          <div className="col-2">
            <Button
              variant="secondary"
              className="btn-sm"
              onClick={handleClearFacets}
              disabled={!filters || filters.length === 0}
              title="Clear all filters"
            >
              Clear
            </Button>
          </div>
        </div>

        <div className="clearfix pr-2 pt-2 pb-0">
          <div className="float-right">
            <PaginatedListNavigation
              columnVisibility={columnVisibility}
              currentPage={pageNo}
              onNextPage={() => setPageNo((p) => (hasNextPage ? p + 1 : p))}
              onPrevPage={() => setPageNo((p) => Math.max(0, p - 1))}
              onToggleColumn={handleToggleColumn}
              totalResults={totalRecords}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={(value) => setRowsPerPage(value)}
            />
          </div>
        </div>

        <div>
          <ChannelPickerTable
            channelList={formattedChannelList}
            columnVisibility={columnVisibility}
            selectedChannelId={selectedChannelId}
            onAddChannel={handleAddChannel}
            sort={sort}
            sortType={sortType}
            onSortChange={handleSortChange}
          />
        </div>

        {isLoading && <small className="form-text text-muted">Loading channels...</small>}
        {error && <small className="form-text text-danger">Failed to load channels.</small>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
      </Modal.Footer>
    </Modal>
  );
};

ChannelSelectorModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedChannelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectChannel: PropTypes.func.isRequired,
  title: PropTypes.string,
  hideChannelTypeSelector: PropTypes.bool,
  endpointUrl: PropTypes.string,
};

ChannelSelectorModal.defaultProps = {
  selectedChannelId: null,
  title: 'Select a Channel',
  hideChannelTypeSelector: false,
  endpointUrl: null,
};

export default ChannelSelectorModal;
